import {
  OneOfElements,
  IProductElement,
  ElementKind,
  IBuilding,
  IElement,
  OneOfChildElements,
  Baseline,
  OneOfParentElements,
  ICountAndUnit,
  IRecipeElement,
  Project,
  IElementID,
  ExpressionValue,
  OneOfPropertyElements,
  OneOfElementListElements,
  OneOfListElements,
  ElementKindMap,
  OneOfProductListChildren,
} from '../models/project.model';
import {
  EMPTY_FROZEN_ARRAY,
  asArray,
  includesSome,
  isDefined,
  isOneOf,
  typeFilter,
} from './array.helpers';
import { cloneDeep, escapeRegExp, isObject, last } from 'lodash';
import {
  isElementExpressionProperty,
  getElementProperties,
} from './element-property.helpers';
import {
  ArrayOrSingle,
  ItemOrItemId,
  NonNullableRequired,
  OptionallyRequired,
} from '../models/type-helpers.model';
import { PathRecord } from './element-category.helpers';
import { getElementQuantityProperties } from './element-quantity.helpers';
import { Recipe } from '../models/recipe.model';
import { isRecipe } from './recipe.helpers';
import {
  getId,
  hasDefinedProperties,
  isNonArrayObject,
} from './object.helpers';
import { isProjectInfoOrFolder } from './project-folder.helpers';
import { IElementProperty } from '../models/element-property.model';
import { isExpressionValue } from './expression-factory.helpers';
import { cacheFactory, inMigrationMode, required } from './function.helpers';
import { requiredKind } from './element-kind.helpers';
import { createElementFromPartial } from './element-factory.helpers';
import { validateProject } from '../validation/project-validation';
import {
  IProductListCategoryGroup,
  IProductListGroup,
  IProductListItem,
} from '../models/product.model';
import { getNewActiveVersionId } from './project-factory.helpers';
import { ProductRecordOwners } from './product.helpers';
import { regenerateIdsInDesign } from './design/design.helpers';
import { projectGuards } from './guards/project-guards.helpers';
import { guards } from './typeguards.helpers';

export type ElementPath = [OneOfParentElements, ...IElement[]] | [];

/**
 * Append an {@link IElement} segment to an {@link ElementPath} for traversal helpers.
 * @param path - Current path (version root plus zero or more elements)
 * @param element - Element to append
 * @returns Path including {@link element}
 */
export const appendElementToPath = (
  path: ElementPath,
  element: IElement,
): ElementPath => [...path, element] as ElementPath;

export type OneOfSearchableElements =
  OneOfElements | IBuilding | Project | Recipe;

type FindFn<T> = (
  el: T,
  path: OneOfParentElements[],
) => boolean | void | undefined;
type ForEachFn<T> = (el: T, path: ElementPath) => any;

const getFindFn = <T extends OneOfElements = OneOfElements>(
  fnOrId: FindFn<T> | IElementID,
): FindFn<T> => {
  if (typeof fnOrId === 'string') {
    // Reuse find function to avoid creating new functions and invalidating other caches
    return cacheFactory(
      () => (el: T) => el.id === fnOrId,
      `getFindFn[${fnOrId}]`,
      [fnOrId],
    );
  }
  return fnOrId;
};

/**
 * Recursive find function works like Array.find
 * but for all elements in element tree. Will include root element (buildingVersion) in the search
 * @param searchIn
 * @param fnOrId Function to find element or id to search for
 * @returns
 */
export const findElement = (
  searchIn: OneOfSearchableElements | undefined,
  fnOrId: FindFn<OneOfElements> | IElementID | undefined,
  path: OneOfParentElements[] = [],
): OneOfElements | undefined => {
  if (!searchIn) {
    return undefined;
  }
  if (!fnOrId) {
    return undefined;
  }
  const fn: FindFn<OneOfElements> = getFindFn(fnOrId);

  if (typeof fn !== 'function') {
    throw new Error('fnOrId must be a function or a string');
  }

  if (isOneOfElements(searchIn) && fn(searchIn, path)) {
    return searchIn;
  } else {
    const children = getSearchableChildren(searchIn);
    const childPath = isOneOfParentElements(searchIn)
      ? [...path, searchIn]
      : path;

    // Use a for loop instead of forEach to avoid nested functions
    // Otherwise "return" will not work properly
    for (const child of children) {
      // Search in the current child
      const result = findElement(child, fn, childPath);

      // Abort search if the element has been found
      if (result !== undefined) {
        return result;
      }
    }
  }
};

type IElementPathAndParent = {
  element?: OneOfElements;
  path: OneOfParentElements[];
  parent?: OneOfParentElements;
};

export const findElementAndParent = <
  T extends boolean = false,
  R = T extends true
    ? NonNullableRequired<IElementPathAndParent>
    : IElementPathAndParent,
>(
  searchIn: OneOfSearchableElements | undefined,
  fnOrId: FindFn<OneOfElements> | IElementID,
  require: T = false as T,
): R => {
  const fn = getFindFn(fnOrId);
  let path: OneOfParentElements[] = [];

  const element = findElement(searchIn, (el, p) => {
    if (fn(el, p)) {
      path = p;
      return true;
    }
  });

  return {
    element: required(element, require),
    path,
    parent: required(last(path), require),
  } as R;
};

/**
 * @deprecated The building layer was flattened into {@link Project} (DEV-2576).
 * Returns the project-level building view (`meta`/`design`/`features`/`baselines`).
 */
export const getBuilding = (
  projectOrBuilding: Project | IBuilding,
): IBuilding => projectOrBuilding;

/**
 * Recursive find function works like Array.find
 * but for all elements in element tree including searchIn element.
 * Will include root element (like buildingVersion) in the search
 * @param searchIn
 * @param fn
 * @returns
 */
export const forEachElement = (
  searchIn: ArrayOrSingle<OneOfSearchableElements> | undefined,
  fn: ForEachFn<OneOfElements>,
  path: ElementPath = [],
): void => {
  if (!searchIn) {
    return;
  }
  // Support array of elements by just rerunning the function for each element
  if (Array.isArray(searchIn)) {
    return searchIn.forEach((el) => forEachElement(el, fn, path));
  }

  if (isOneOfElements(searchIn)) {
    fn(searchIn, path);
  }
  const childPath = isOneOfParentElements(searchIn)
    ? [...path, searchIn]
    : path;

  getSearchableChildren(searchIn).forEach((e) =>
    forEachElement(e, fn, childPath as ElementPath),
  );
};

/**
 * Filter elements recursively. Will include root element (buildingVersion) in the search
 * @param searchIn
 * @param fn
 * @param searchExcludedChildren If false, children of excluded elements will not be searched
 * @returns
 */
export const filterElements = (
  searchIn: ArrayOrSingle<OneOfSearchableElements>,
  fn: FindFn<OneOfElements>,
  searchExcludedChildren = true,
): OneOfElements[] => {
  const elements: OneOfElements[] = [];
  const excludedElements: OneOfElements[] = [];

  forEachElement(searchIn, (el, path) => {
    // If a parent element is excluded, don't search children (unless searchExcludedChildren is true)
    if (!searchExcludedChildren && includesSome(excludedElements, ...path)) {
      return;
    }

    if (fn(el, path)) {
      elements.push(el);
    }
    // Store excluded elements to avoid searching children
    else {
      excludedElements.push(el);
    }
  });

  return elements;
};

/**
 * Get all parents of a certain element as an array (excluding self).
 * First item in path is a Baseline if searchIn is a project, building or version.
 * Return undefined if element is not found.
 * @param root
 * @param elementOrId
 * @returns
 */
export const getPathToElement = <
  T extends boolean = true,
  R = OptionallyRequired<ElementPath, T>,
>(
  searchIn: OneOfSearchableElements | ElementPath,
  elementOrId: ItemOrItemId<OneOfElements> | undefined,
  throwIfNotFound: T = true as T,
): R => {
  // If a path is passed in, return it
  if (Array.isArray(searchIn)) {
    return searchIn as R;
  }
  let currentPath: OneOfParentElements[] | undefined;
  let elementId: string | undefined = undefined;
  if (elementOrId) {
    elementId = getId(elementOrId);
    findElement(searchIn, (e, path) => {
      // Find by id to avoid comparing objects (will be more tolerant to changes)
      if (e.id === elementId) {
        currentPath = path;
        return true;
      }
    });
  }
  if (throwIfNotFound && !currentPath) {
    let searchInData: string[] = [];
    if (isProject(searchIn)) {
      searchInData = [`type[project]`, `id[${searchIn.id}]`];
    } else if (isBuilding(searchIn)) {
      searchInData = [
        `type[building]`,
        `baselines[${searchIn.baselines.map((v) => v.id).join(', ')}]`,
      ];
    } else {
      const type = getElementKind(searchIn);
      searchInData = [
        `type[${type}]`,
        `id[${searchIn.id}]`,
        `name[${searchIn.name}]`,
      ];
    }
    const errorText = [
      `getPathToElement(): Could not get path of element. Element not found`,
      `elementId: ${elementId}`,
      `searchIn: ${searchInData.join(' - ')}`,
    ].join('\n');
    throw new Error(errorText);
  }
  return currentPath as R;
};

/**
 * Get element in tree structure by id
 * @param searchIn Element to search in
 * @param id Id to search for
 * @param require Pass true to throw if element is not found. Pass a kind to require a specific kind of element
 * @returns
 */
export const getElementById = <
  T extends boolean | keyof ElementKindMap = false,
  R = T extends ElementKind
    ? ElementKindMap[T]
    : OptionallyRequired<OneOfElementListElements, T>,
>(
  searchIn: OneOfSearchableElements | undefined,
  id: OneOfElements['id'] | undefined,
  require: T = false as T,
): R => {
  // Migration mode disables cacheFactory, so the map would rebuild (a full tree
  // walk) on every call — worse than findElement's early-exit. Fall back to the
  // direct scan there; the O(1) map only pays off when the cache is live.
  const element = id
    ? inMigrationMode()
      ? findElement(searchIn, id)
      : getElementByIdMap(searchIn).get(id)
    : undefined;

  if (typeof require === 'string') {
    return requiredKind(element, require) as R;
  }
  return required(element, require);
};

/**
 * Cached `id → element` map for a search root, built with a single tree walk.
 * Replaces the per-id {@link findElement} scan in {@link getElementById}: n
 * lookups against the same root cost O(n) total instead of O(n²) (the table
 * resolves every visible row against the same version). First occurrence wins,
 * matching {@link findElement}'s DFS first match. Invalidated — like the
 * previous per-id cache — when the `searchIn` object identity changes.
 */
const getElementByIdMap = (
  searchIn: OneOfSearchableElements | undefined,
): Map<OneOfElements['id'], OneOfElements> =>
  cacheFactory(
    () => {
      const map = new Map<OneOfElements['id'], OneOfElements>();
      forEachElement(searchIn, (el) => {
        if (!map.has(el.id)) map.set(el.id, el);
      });
      return map;
    },
    `getElementByIdMap[${getSearchableElementCacheKey(searchIn)}]`,
    [searchIn],
  );

const getSearchableElementCacheKey = (
  searchIn: OneOfSearchableElements | undefined,
): string => {
  if (!searchIn) {
    return 'undefined';
  }
  // Project also matches isBuilding after flatten (`baselines` lives on Project).
  if (isProject(searchIn)) {
    return String(searchIn.id);
  }
  if (isBuilding(searchIn)) {
    return 'building';
  }
  return String(searchIn.id);
};

/**
 * Similar to getElementById but also returns the path to the element
 * @param searchIn Which element to search in
 * @param id optional id to search for
 * @param requireResult If true, will throw if element is not found
 * @returns
 */
export const getPathAndElementById = <T extends boolean = false>(
  searchIn: OneOfSearchableElements | undefined,
  id: OneOfElements['id'] | undefined,
  requireResult: T = false as T,
) => findElementAndParent(searchIn, required(id, requireResult), requireResult);

/**
 * Get parent of an element if it exists (versions won't have a parent)
 * @param searchIn
 * @param element
 * @returns
 */
export const getParentElement = (
  searchIn: OneOfSearchableElements,
  elementOrId: ItemOrItemId<OneOfElements> | undefined,
): OneOfParentElements | undefined =>
  elementOrId ? last(getPathToElement(searchIn, elementOrId)) : undefined;

export const getBuildingVersionFromChild = (
  project: Project | IBuilding | Baseline,
  child: OneOfElements,
): Baseline => {
  const version = getPathToElement(project, child)[0];
  if (!isBuildingVersionElement(version)) {
    throw new Error('Could not find building version for child');
  }
  return version;
};

/**
 * Get Main Category of an element (recursive)
 * @param version
 * @param element
 * @returns
 */
export const getMainCategoryElementAncestor = <T extends OneOfElements>(
  version: Baseline,
  element: T | undefined,
): IElement | undefined => {
  const parent = getParentElement(version, element);

  if (isElement(parent) && parent?.category_id) {
    return parent;
  } else if (
    isBuildingVersionElement(element) ||
    isBuildingVersionElement(parent) ||
    !parent
  ) {
    return undefined;
  } else {
    return getMainCategoryElementAncestor(version, parent);
  }
};

/** Module-scope predicate so {@link flattenElements} avoids allocating it per call. */
const isUndefinedId = (id: string | undefined): boolean => id === undefined;

/**
 * Get all elements (with their children) in a flat array, in element-list order.
 *
 * Results are memoized per id-set via {@link cacheFactory} (key `flattenElements[<ids>]`).
 * Elements with an `undefined` id bypass the cache: their ids would collapse to empty
 * key segments and collide with other element sets (see the comment in the body) — never
 * pass half-built factory drafts whose ids are not yet stamped.
 *
 * @param elements - Root elements to flatten (undefined entries are ignored).
 * @returns Flat array of every element and descendant.
 */
export const flattenElements = <
  T extends OneOfSearchableElements,
  R = T extends OneOfChildElements ? OneOfChildElements : OneOfElements,
>(
  ...elements: (T | undefined)[]
): R[] => {
  const searchIn = getSearchRoot(...elements);
  const ids = searchIn.map((el) => el.id);

  const compute = (): R[] =>
    getSearchRoot(...elements)
      .map((element) => [
        element,
        ...flattenElements(...getChildElements(element)),
      ])
      .flat() as R[];

  // Bypass the cache when any id is undefined. Undefined ids collapse to empty
  // key segments (`ids.join(',')`), so distinct element sets collide on the same
  // cache key — most dangerously the empty `flattenElements[]` key shared by every
  // leaf flatten — poisoning later reads with phantom undefined-id elements
  // (surfaces as "Duplicate ids in project" in validateProject). DEV-3386.
  if (ids.some(isUndefinedId)) return compute();

  return cacheFactory(compute, `flattenElements[${ids.join(',')}]`, searchIn);
};

export const getAllProductElements = (
  ...element: (OneOfSearchableElements | undefined)[]
): IProductElement[] => {
  return flattenElements(...element).filter(isProductElement);
};

export const getAllRecipeElements = (
  recipe?: Recipe,
  ...element: OneOfSearchableElements[]
): IElement[] => {
  return flattenElements(...element)
    .filter(isElement)
    .filter((recipeElement) => recipeElement.recipe_id === recipe?.id);
};

/**
 * Check if element is a parent element (has a defined .elements property)
 * @param element
 * @returns
 */
export const isParentElement = <T extends OneOfListElements = IElement>(
  element: OneOfSearchableElements | OneOfListElements | undefined,
): element is OneOfParentElements<T> => {
  return !!element && 'elements' in element && !!element.elements;
};

export const isOneOfParentElements = (
  element?: OneOfSearchableElements | OneOfListElements,
): element is OneOfParentElements => {
  return isElement(element) || isBuildingVersionElement(element);
};

export const isOneOfElements = (
  element?: OneOfSearchableElements | OneOfListElements,
): element is OneOfElements => {
  return isOneOfChildElements(element) || isBuildingVersionElement(element);
};

export const isOneOfChildElements = (
  element?: OneOfSearchableElements | OneOfListElements,
): element is OneOfChildElements => {
  return (
    isElement(element) ||
    isProductElement(element) ||
    isProjectInfoOrFolder(element)
  );
};

export const isOneOfPropertyElements = (
  element?: OneOfSearchableElements,
): element is OneOfPropertyElements =>
  isElement(element) || isBuildingVersionElement(element);

export const isProject = (element?: unknown): element is Project => {
  return (
    isObject(element) &&
    hasDefinedProperties(
      element as Project,
      'baselines',
      'name',
      'id',
      'owner',
    ) &&
    guards.is.array((element as Project).baselines)
  );
};

/**
 * @deprecated The building layer was flattened into {@link Project} (DEV-2576).
 * Also matches {@link Project} because `baselines` now lives there. Use
 * {@link isProject} first when the two must be distinguished.
 */
export const isBuilding = (
  element?: OneOfSearchableElements,
): element is IBuilding => {
  return (
    isObject(element) &&
    'baselines' in element &&
    guards.is.array(element.baselines)
  );
};

export const isElementKind = (kind?: unknown): kind is ElementKind => {
  return typeof kind === 'string' && isOneOf(ElementKind, kind);
};

/**
 * @deprecated Use projectGuards.is.buildingVersion instead
 * @param element
 * @returns
 */
export const isBuildingVersionElement = (
  element?: unknown,
): element is Baseline => projectGuards.is.buildingVersion(element);

/**
 * @deprecated Use projectGuards.is.productElement instead
 * @param element
 * @returns
 */
export const isProductElement = (
  element?: unknown,
): element is IProductElement => projectGuards.is.productElement(element);

export const isRecipeElement = (
  element?: OneOfElements,
): element is IRecipeElement => {
  return projectGuards.is.element(element) && !!element.recipe_id;
};

/**
 * @deprecated Use projectGuards.is.element instead
 * @param element
 * @returns
 */
export const isElement = (element?: unknown): element is IElement =>
  projectGuards.is.element(element);

export const isDeletedElement = (element?: unknown) =>
  isElement(element) && !!element.isDeleted;

/**
 * TODO: Check if element have count and unit
 * @param elementOrProperty
 * @returns
 */
export const isCountAndUnitElement = <
  T extends OneOfElementListElements | ICountAndUnit | IElementProperty,
>(
  elementOrProperty?: T,
): elementOrProperty is T & Required<ICountAndUnit> => {
  return (
    !isElement(elementOrProperty) &&
    hasDefinedProperties(elementOrProperty as ICountAndUnit, 'count', 'unit') &&
    isExpressionValue((elementOrProperty as ICountAndUnit).count)
  );
};

export const isProductListCategoryGroup = (
  element: unknown,
): element is IProductListCategoryGroup =>
  getElementKind(element) === ElementKind.ProductCategory;

export const isProductListGroup = (
  element: unknown,
): element is IProductListGroup =>
  getElementKind(element) === ElementKind.ProductGroup;

export const isProductListItem = (
  element: unknown,
): element is IProductListItem =>
  getElementKind(element) === ElementKind.ProductItem;

export const isProductListElement = (
  element: unknown,
): element is OneOfProductListChildren =>
  isProductListCategoryGroup(element) ||
  isProductListGroup(element) ||
  isProductListItem(element);

/**
 * Get root children of all versions
 * @param versions
 * @returns
 */
export const getChildElementsOfVersions = (
  ...versions: Baseline[]
): IElement[] => {
  return versions.reduce((elements, version) => {
    // TODO: Now it should only be IElement as childs of Versions.
    elements.push(...getChildElements(version).filter(isElement));
    return elements;
  }, [] as IElement[]);
};

/**
 * Helper to avoid having to if case products all the time
 * @param element
 * @returns
 */
export const getChildElements = <T extends OneOfListElements>(
  element?: T,
): OneOfChildElements<T>[] => {
  if (!isParentElement<T>(element)) return [];
  return (element.elements ?? []) as OneOfChildElements<T>[];
};

/**
 * Check if element has direct children (not including grandchildren etc)
 * @param element
 * @returns
 */
export const hasChildren = (element?: OneOfListElements): boolean =>
  getChildElements(element).length > 0;

/**
 * Check if child is a direct child of parent.
 * @param parent
 * @param childOrId
 * @returns
 */
export const isChildOf = (
  parent: OneOfParentElements,
  childOrId: ItemOrItemId<OneOfChildElements>,
): boolean => isOneOfById(getChildElements(parent), childOrId);

/**
 * Check if element exist in array of elements.
 * Use id to check to not strict compare objects
 * @param elements
 * @param elementOrId
 * @returns
 */
export const isOneOfById = (
  elements: OneOfElements[],
  elementOrId: ItemOrItemId<OneOfElements>,
): boolean => {
  const elementId = getId(elementOrId);
  return elements.some(({ id }) => id === elementId);
};

/**
 * Get root elements that can be used by findElements/flattenElements etc.
 * @param element
 * @returns
 */
const getSearchRoot = (
  ...elements: (OneOfSearchableElements | undefined)[]
): OneOfElements[] =>
  elements
    .filter(isDefined)
    .map((element) => {
      if (isRecipe(element)) {
        return element.elements ?? [];
      }
      return isOneOfElements(element)
        ? [element]
        : getAllBuildingVersions(element);
    })
    .flat();

/**
 * Get children of element, if element is a building or project it will return all versions
 * @param element
 * @returns
 */
export const getSearchableChildren = (
  element: OneOfSearchableElements | undefined,
): OneOfElements[] => {
  if (isRecipe(element)) {
    return element.elements ?? [];
  }
  return isOneOfElements(element)
    ? getChildElements(element)
    : getAllBuildingVersions(element);
};

/**
 * Get elements who is parent from the list of elements, IE. Ignoring products
 * Note that it filters on type so an empty array also counts as children.
 * @param element
 */
export const getChildrenWithChildren = (
  element: OneOfElements,
): OneOfParentElements[] => {
  return typeFilter(getChildElements(element), isParentElement);
};

export const getAllBuildingVersions = (
  versionParent: Project | IBuilding | Baseline[] | Baseline | undefined,
): Baseline[] => {
  if (!versionParent) {
    return EMPTY_FROZEN_ARRAY as Baseline[];
  }
  if (Array.isArray(versionParent)) {
    return versionParent;
  }
  if (isBuildingVersionElement(versionParent)) {
    return [versionParent];
  }

  // Both Project and the deprecated IBuilding view expose `baselines`.
  return versionParent.baselines ?? (EMPTY_FROZEN_ARRAY as Baseline[]);
};

export const getBuildingVersionById = <
  T extends true | false | undefined = false,
  R = T extends true ? Baseline : Baseline | undefined,
>(
  projectOrBuilding: Project | IBuilding | Baseline[] | undefined,
  id: IElementID | undefined,
  throwIfNotFound: T = false as T,
): R => {
  const version = getAllBuildingVersions(projectOrBuilding).find(
    (v) => v.id === id,
  );
  if (throwIfNotFound && !version) {
    throw new Error('Could not find version');
  }
  return version as R;
};

/**
 * Regenerate ids for all elements in a project.
 * Will also remap selections in proposals
 * @param root A project, building or version to regenerate ids in
 * @returns
 */
export const regenerateIds = (project: Project): Project => {
  const newProject: Project = {
    ...project,
    baselines: project.baselines.map((version) =>
      createElementFromPartial(version, true),
    ),
    design: regenerateIdsInDesign(project.design),
  };

  const newVersions = getAllBuildingVersions(newProject);
  const oldVersions = getAllBuildingVersions(project);

  const newActiveVersionId = getNewActiveVersionId(
    project.active_version_id,
    oldVersions,
    newVersions,
  );

  return validateProject({
    ...newProject,
    active_version_id: newActiveVersionId,
  });
};

/**
 *
 * @param versions
 */
export const regenerateIdsInVersion = (version: Baseline): Baseline => {
  const clone = cloneDeep(version);

  return clone;
};

export const getElementKind = (element?: unknown): ElementKind | undefined =>
  isNonArrayObject(element) &&
  'kind' in element &&
  typeof element.kind === 'string'
    ? (element.kind as ElementKind)
    : undefined;

/**
 * Get all expression values (property.[count/fallbackCount], element.count) from an element or project (recipe)
 * @param root
 * @returns
 */
export const getAllElementExpressionValues = (
  root: OneOfSearchableElements,
): ExpressionValue[] => {
  const allExpressionValues: ExpressionValue[] = [];

  flattenElements(root).forEach((element: OneOfElements) => {
    if ('count' in element && element.count && 'expression' in element.count) {
      allExpressionValues.push(element.count);
    }
    [...getElementProperties(element), ...getElementQuantityProperties(element)]
      .filter(isElementExpressionProperty)
      .forEach(({ count, fallbackCount }) => {
        if (count !== undefined) {
          allExpressionValues.push(count);
        }
        if (fallbackCount !== undefined) {
          allExpressionValues.push(fallbackCount);
        }
      });
  });

  return allExpressionValues;
};

export const getReplacedExpression = (
  expression: string,
  variablesToReplace: string[],
  replacement: string,
): string =>
  expression.replaceAll(
    new RegExp(
      `(?<=[^a-zåäö0-9_"']|^)(${variablesToReplace
        .map(escapeRegExp)
        .join('|')})(?=[^a-zåäö0-9_"']|$)`,
      'ig',
    ),
    replacement,
  );

/**
 * Replace variables in expressions.
 * Note: This will modify the project in place so be careful
 * @param project
 * @param replacements
 */
export const replaceExpressionVariables = (
  project: Project | Recipe,
  replacements: { variablesToReplace: string[]; replacement: string }[],
): void => {
  const allElementExpressionValues = getAllElementExpressionValues(project);

  replacements.forEach(({ variablesToReplace, replacement }) =>
    allElementExpressionValues.forEach((value) => {
      value.expression = getReplacedExpression(
        value.expression,
        variablesToReplace,
        replacement,
      );
    }),
  );
};

/**
 * Get how many levels of children an element have
 * @param element
 * @returns
 */
export const getElementChildrenDepth = (element?: OneOfElements): number => {
  let depth = 0;
  forEachElement(element, (_el, path) => {
    depth = Math.max(depth, path.length);
  });
  return depth;
};

/**
 * Get the path down to each product element
 * @param rootElements One or more elements to search from
 * @param getPathToAllElements Also get path for root elements (except version elements)
 * @returns
 */
export const getProductElementPathsRecord = (
  rootElements: ArrayOrSingle<OneOfElements> | undefined,
  getPathToAllElements = false,
): PathRecord => {
  const record = {} as Record<IElementID, OneOfParentElements[]>;

  asArray(rootElements).forEach((root) => {
    forEachElement(root, (el, path) => {
      const hasProductElements = flattenElements(el).some(isProductElement);
      const isRootElement =
        !isBuildingVersionElement(el) && !hasProductElements;

      if ((getPathToAllElements && isRootElement) || isProductElement(el)) {
        record[el.id] = path;
      }
    });
  });

  return record;
};

export const getBuildingVersionFromProductRecordOwners = (
  productOwner: ProductRecordOwners,
): Baseline | undefined => {
  if (Array.isArray(productOwner)) {
    return isBuildingVersionElement(productOwner[0])
      ? productOwner[0]
      : undefined;
  }
  return isBuildingVersionElement(productOwner) ? productOwner : undefined;
};

export const getBuildingVersionFromPath = (
  path: ElementPath | Baseline,
): Baseline =>
  isBuildingVersionElement(path)
    ? path
    : requiredKind(path[0], ElementKind.Version);

const isValidElementPath = (
  path: OneOfElements[] | ElementPath,
): path is ElementPath => {
  if (path.length === 0) {
    return true;
  }
  return path.every((e, i) => {
    const inParent = i > 0 ? isOneOf(getChildElements(path[i - 1]), e) : true;
    return (
      inParent &&
      (i === 0 ? isBuildingVersionElement(e) || isElement(e) : isElement(e))
    );
  });
};

export const validatePath = (
  path: OneOfElements[] | ElementPath,
): ElementPath => {
  if (!isValidElementPath(path)) {
    throw new Error('Invalid path');
  }
  return path;
};
